define("apps/classrooms/views/pdf-utils/pdf-mixin", [
    "vendor/jszip/jszip",
    "vendor/jszip-utils/jszip-utils",
    "vendor/jsfilesaver/jsfilesaver",
    "apps/classrooms/views/pdf-utils/error-report-pdf",
    "apps/classrooms/views/pdf-utils/instruction-pdf",
],
    /*eslint strict: [0, "function"]*/
    function(
        JSZip,
        jsZipUtils,
        jsFilesaver,
        errorReportPdf,
        instructionPdf
    ) {
        return {
            /**
             * PDF TARGET TYPES: student|classroom|site
             */
            TARGET_TYPES: {
                "STUDENT": "student",
                "CLASSROOM": "classroom",
                "SITE": "site",
            },

            /**
             * Adds instruction PDF to the zip file with QR code login instructions
             */
            addInstructionPdf: function(zip) {
                if (!zip) {
                    zip = new JSZip();
                }

                var countryCode = this.model.get('countryCode');
                var instructionPDF = instructionPdf(countryCode);

                zip.file(
                    'QR-Code-Instructions.pdf',
                    instructionPDF.output('blob')
                );

                return zip;
            },

            /**
             * Checks model to see if there are students that could not have
             *  qr codes generated. If so, create a new error report pdf and
             *  attach to new or existing zip file.
             */
            checkErrorsAndReport: function(zip) {
                if ((this.model.get('studentsWithQRFailures') || []).length > 0) {
                    if (!zip) {
                        zip = new JSZip();
                    }

                    var errorReportPDF = errorReportPdf(model);

                    zip.file(
                        'Error-Report.pdf',
                        errorReportPDF.output('blob')
                    );
                }

                return zip;
            },

            /**
             * Force a zipped pdf download. This method is currently being used to
             *  transition from qr code v1 to qr code v2. You probably shouldnt
             *  rely on this method always existing.
             */
            forcePDFDownload: function(pdf, fileName, zip) {
                if (!zip) {
                    zip = new JSZip();
                }

                zip.file(
                    fileName + '.pdf',
                    pdf.output('blob')
                );

                // Add instruction PDF to zip
                zip = this.addInstructionPdf(zip);

                zip.generateAsync({
                    "type": 'blob'
                }).then(function(content) {
                    saveAs(content, fileName + '.zip');
                });
            },

            /**
             * Backwards compat admin check
             */
            isAdmin: function() {
                return this.model.get('isAdmin') || this.model.get('multiClass');
            },

            /**
             * Checks if we are targeting a single student
             */
            isStudent: function() {
                return Boolean(this.model.get('student'));
            },

            /**
             * Checks if we are targeting an entire site, i.e. multi teachers in multiple classrooms.
             */
            isSite: function() {
                return this.isAdmin(this.model) &&
                       !this.isClassroom(this.model) &&
                       !this.isStudent(this.model);
            },

            /**
             * Checks if we are targeting a single student
             */
            isClassroom: function() {
                return !this.isStudent(this.model) && Boolean(this.model.get('classroom'));
            },

            /**
             * Trigger a PDF object to save.
             */
            savePDF: function(pdf, name) {
                pdf.save(name + '.pdf');
            },

            /**
             * Write pdf file to zip. Check to make sure we havent already written
             *  the pdf to zip. If not, add file to zip. If already have the file,
             *  skip and return the existing zip.
             */

            writeFileToZip(zip, filePath, pdf) {
                if (!zip.files.hasOwnProperty(filePath)) {
                    zip.file(
                        filePath,
                        pdf.output('blob')
                    );
                }

                return zip;
            },

            /**
             * Derive the student name portion of a qr code. If we have a first
             *  name and last name, lets return their full name. If we have the last
             *  but no first name, lets return the last name. If the student is missing
             *  both names or we only have their first name, we dont want to include
             *  that in the label, so we return nothing.
             */
            getStudentLabelName: function(student) {
                if (student.first_name && student.last_name) {
                    return student.first_name + ' ' + student.last_name;
                } else if (!student.first_name && student.last_name) {
                    return student.last_name;
                }

                return null;
            },

            /**
             * For individual student qr codes, we use the students name
             *  for the zip file and contained pdf. If the student does not
             *  have their first and last name, lets fall back to site or classroom
             *  name.
             */
            getStudentFileName: function(student) {
                if (student.first_name && student.first_name) {
                    return student.first_name + '_' + student.first_name;
                }

                var classroom = this.model.get('classroom');

                return classroom ? classroom.name : this.model.get('site').name;
            },

             /**
             * Returns specific pdf target type, i.e. student, classroom, or site.
             */
            getTargetType: function() {
                var targetType = this.TARGET_TYPES.CLASSROOM;

                if (this.isStudent(this.model)) {
                    targetType = this.TARGET_TYPES.STUDENT;
                } else if (this.isSite(this.model)) {
                    targetType = this.TARGET_TYPES.SITE;
                }

                return targetType;
            },

            /**
             * Builds a specific pdf target type. Return type is always PDF object, but we
             *  can optionally trigger a zip/pdf download.
             */
            buildTargetTypePDF: function(isZip, forcePDFFormat) {
                var targetType = this.getTargetType(),
                    pdf;

                switch (targetType) {
                    // classroom specifc preview/download.
                    case this.TARGET_TYPES.CLASSROOM:
                        if (!isZip) {
                            return this.buildLayout();
                        } else {
                            var pdf = this.buildLayout();

                            if (forcePDFFormat) {
                                var zip = this.checkErrorsAndReport();
                                zip = this.addInstructionPdf(zip);
                                this.forcePDFDownload(
                                    pdf,
                                    this.model.get('classroom').name,
                                    zip
                                );
                            } else {
                                this.savePDF(pdf, this.model.get('classroom').name);
                            }
                        }
                        break;

                    // single student preview/download.
                    case this.TARGET_TYPES.STUDENT:
                        if (!isZip) {
                            return this.buildLayout();
                        } else {
                            var stud = this.model.get('printStudents')[0],
                                pdf = this.buildLayout(),
                                fileName = this.getStudentFileName(stud, this.model.get('site'));

                            if (forcePDFFormat) {
                                var zip = this.checkErrorsAndReport();
                                zip = this.addInstructionPdf(zip);
                                this.forcePDFDownload(
                                    pdf,
                                    fileName,
                                    zip
                                );
                            } else {
                                this.savePDF(pdf, fileName);
                            }
                        }
                        break;
                    // admin site preview/download. downloads structured by teacher;
                    case this.TARGET_TYPES.SITE:
                        if (!isZip) {
                            return this.buildLayout();
                        } else {
                            var zip = new JSZip();

                            // add all the individual student PDFs to zip
                            zip = this.buildLayout(null, isZip, zip, 'All Students');

                            var teachers = this.model.get('teachers'),
                                site = this.model.get('site'),
                                account = this.model.get('account'),
                                siteName = (site) ? site.name : account.name;

                            // go through each classroom and place the students in the correct
                            //  folder
                            $.each(Object.keys(teachers), _.bind(
                                function(_, teacherId) {
                                    var teacher = teachers[teacherId],
                                        folderName;

                                    // if the teacher doesnt have any students, skip to next teacher.
                                    if ((teacher.students || []).length === 0) {
                                        return;
                                    }

                                    folderName = teacher.details.first_name + ' ' + teacher.details.last_name;

                                    zip = this.buildLayout(
                                        teacher.students,
                                        isZip,
                                        zip,
                                        folderName
                                    );
                                }, this)
                            );

                            zip = this.checkErrorsAndReport(zip);

                            // Add instruction PDF to zip
                            zip = this.addInstructionPdf(zip);

                            zip.generateAsync({
                                "type": 'blob'
                            }).then(function(content) {
                                saveAs(content, siteName + '.zip');
                            });
                        }
                        break;
                    default:
                        return pdf;
                }

                return pdf;
            },
        };
    }
);

